import {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CreateGroupPayload,
  CreateGroupResponse,
  DeactivateGroupPayload,
  DeleteMemberInGroupPayload,
  Groups,
  GroupsApi,
  UpdateGroupPayload,
} from 'api/groups';
import { ALL_MEMBERS_QUERY_KEY } from './members';

export const GroupsListQueryKey = ['user-groups'];

export const useGroupsQuery = (
  queryOptions: UseQueryOptions<
    Groups[],
    unknown,
    Groups[],
    typeof GroupsListQueryKey
  > = {},
): UseQueryResult<Groups[]> => {
  return useQuery(GroupsListQueryKey, () => GroupsApi.getGroups(), {
    ...queryOptions,
    cacheTime: queryOptions?.cacheTime ?? 0,
  });
};

export const getGroupByUserQueryKeyUsingId = (
  userId: number,
): [string, number] => ['user-group', userId];

export const useGroupsByUserQuery = (
  userId: number,
  options: UseQueryOptions<Groups[], unknown, Groups[], [string, number]> = {},
) =>
  useQuery(
    getGroupByUserQueryKeyUsingId(userId),
    () => GroupsApi.getGroupsByUser(userId),
    {
      ...options,
    },
  );

export const getGroupDetailQueryKeyUsingId = (
  groupId: number,
): [string, number] => ['user-group', groupId];

export const useGroupDetailQuery = (groupId: number, options = {}) =>
  useQuery(
    getGroupDetailQueryKeyUsingId(groupId),
    () => GroupsApi.getGroupDetail(groupId),
    {
      ...options,
      cacheTime: 0,
    },
  );

export const useCreateGroupMutation = (): UseMutationResult<
  CreateGroupResponse,
  unknown,
  CreateGroupPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => GroupsApi.createGroup(payload),
    onSuccess: () => {
      queryClient.refetchQueries(['user-group']);
      queryClient.refetchQueries(ALL_MEMBERS_QUERY_KEY);
    },
  });
};

export const useDeactivateGroupMutation = (): UseMutationResult<
  Groups[],
  unknown,
  DeactivateGroupPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId) => GroupsApi.deactivateGroup(groupId),
    onSuccess: () => {
      queryClient.refetchQueries(['user-group']);
      queryClient.refetchQueries(ALL_MEMBERS_QUERY_KEY);
    },
  });
};

export const useUpdateGroupMutation = (): UseMutationResult<
  Groups,
  unknown,
  UpdateGroupPayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => GroupsApi.updateGroup(payload),
    onSuccess: () => {
      queryClient.refetchQueries(['user-group']);
      queryClient.refetchQueries(ALL_MEMBERS_QUERY_KEY);
    },
  });
};

export const useDeleteMembersInGroupMutation = (
  groupId: number,
): UseMutationResult<void, unknown, DeleteMemberInGroupPayload> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => GroupsApi.deleteMemberInGroup(payload),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries(['user-group', groupId]),
        queryClient.invalidateQueries(['user-groups']),
      ]),
  });
};
